import { useState } from "react";
import { api } from "../api/client";
import type { BugReportRow } from "../api/types";
import { PageHeader } from "../components/PageHeader";
import { EmptyView, ErrorView, LoadingView } from "../components/StateView";
import { absoluteTime, relativeTime, shortDeviceId } from "../format";
import { useApi } from "../useApi";
import styles from "./BugReportsPage.module.css";

export function BugReportsPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const state = useApi(() => api.feed("bug-report", { limit: 500 }), []);

  const selected =
    state.kind === "ready"
      ? state.data.rows.find((r) => r.id === selectedId) ?? null
      : null;

  return (
    <>
      <PageHeader
        title="Bug reports"
        subtitle="User-submitted reports, newest first."
      />

      {state.kind === "loading" ? <LoadingView /> : null}
      {state.kind === "error" ? (
        <ErrorView message={state.message} onRetry={state.reload} />
      ) : null}
      {state.kind === "ready" ? (
        state.data.rows.length === 0 ? (
          <EmptyView label="No bug reports yet — nice." />
        ) : (
          <div className={styles.layout}>
            <ul className={styles.list}>
              {state.data.rows.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(r.id)}
                    className={
                      r.id === selectedId
                        ? `${styles.row} ${styles.rowActive}`
                        : styles.row
                    }
                  >
                    <span className={styles.rowTitle}>{r.title}</span>
                    <span className={styles.rowMeta}>
                      <span title={absoluteTime(r.created_at)}>
                        {relativeTime(r.created_at)}
                      </span>
                      {r.suite_version ? <span> · v{r.suite_version}</span> : null}
                      {r.os ? <span> · {r.os}</span> : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <section className={styles.detail}>
              {selected ? (
                <ReportDetail row={selected} />
              ) : (
                <div className={styles.detailPlaceholder}>
                  Select a report to view details.
                </div>
              )}
            </section>
          </div>
        )
      ) : null}
    </>
  );
}

interface DiagAppRow {
  id: string;
  name: string;
  version: string | null;
  brokenAt?: number;
  brokenError?: string;
}

interface DiagStoreRow {
  appId: string;
  dbName: string;
  schemaVersion: number | null;
  tableCount?: number | null;
}

interface DiagLogEntry {
  ts: number;
  kind: string;
  message: string;
  payload?: Record<string, unknown>;
}

interface DiagCrash {
  appId?: string;
  appName?: string;
  brokenVersion?: string;
  errorMessage?: string;
}

interface DiagOrigin {
  source?: string;
}

interface ParsedDiagnostics {
  os?: string;
  arch?: string;
  cpuCount?: number;
  totalRamGb?: number;
  installedApps?: DiagAppRow[];
  stores?: DiagStoreRow[];
  recentLogs?: DiagLogEntry[];
  crash?: DiagCrash;
  origin?: DiagOrigin;
  /** Any other free-form fields older reports might carry. */
  [key: string]: unknown;
}

function ReportDetail({ row }: { row: BugReportRow }) {
  const diagnostics = row.diagnostics
    ? (safeParse(row.diagnostics) as ParsedDiagnostics | null)
    : null;
  const crash = diagnostics?.crash;
  const origin = diagnostics?.origin;
  const installedApps = diagnostics?.installedApps ?? [];
  const stores = diagnostics?.stores ?? [];
  const recentLogs = diagnostics?.recentLogs ?? [];
  return (
    <article>
      <h2 className={styles.detailTitle}>{row.title}</h2>
      <dl className={styles.metaList}>
        <MetaRow label="Submitted" value={absoluteTime(row.created_at)} />
        <MetaRow label="Suite" value={row.suite_version ?? "—"} />
        <MetaRow label="OS" value={`${row.os ?? "—"} ${row.arch ?? ""}`.trim()} />
        <MetaRow label="Device" value={shortDeviceId(row.device_id)} />
        {origin?.source ? (
          <MetaRow label="Reported from" value={origin.source} />
        ) : null}
      </dl>

      {crash ? (
        <div className={styles.crashCallout}>
          <span className={styles.crashCalloutLabel}>Crash context</span>
          <div className={styles.crashCalloutBody}>
            <strong>{crash.appName ?? crash.appId ?? "unknown app"}</strong>
            {crash.brokenVersion ? ` v${crash.brokenVersion}` : null}
            {crash.errorMessage ? (
              <>
                {" "}— <code>{crash.errorMessage}</code>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      <h3 className={styles.sectionHeading}>Description</h3>
      <pre className={styles.body}>{row.description}</pre>

      {row.screenshot_key ? (
        <>
          <h3 className={styles.sectionHeading}>Screenshot</h3>
          <a
            href={api.attachmentUrl(row.screenshot_key)}
            target="_blank"
            rel="noreferrer"
            className={styles.screenshotLink}
          >
            <img
              src={api.attachmentUrl(row.screenshot_key)}
              alt="bug report screenshot"
              className={styles.screenshot}
            />
          </a>
        </>
      ) : null}

      {installedApps.length > 0 ? (
        <>
          <h3 className={styles.sectionHeading}>
            Installed apps ({installedApps.length})
          </h3>
          <table className={styles.diagTable}>
            <thead>
              <tr>
                <th>App</th>
                <th>Version</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {installedApps.map((a) => (
                <tr key={a.id}>
                  <td>
                    <div className={styles.appCellName}>{a.name}</div>
                    <div className={styles.appCellId}>{a.id}</div>
                  </td>
                  <td className={styles.appCellMono}>
                    {a.version ?? "built-in"}
                  </td>
                  <td>
                    {a.brokenAt ? (
                      <span
                        className={styles.appCellBroken}
                        title={a.brokenError}
                      >
                        broken
                      </span>
                    ) : (
                      <span className={styles.appCellOk}>ok</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      {stores.length > 0 ? (
        <>
          <h3 className={styles.sectionHeading}>
            Local databases ({stores.length})
          </h3>
          <table className={styles.diagTable}>
            <thead>
              <tr>
                <th>App</th>
                <th>DB name</th>
                <th>Schema</th>
                <th>Tables</th>
              </tr>
            </thead>
            <tbody>
              {stores.map((s, i) => (
                <tr key={`${s.appId}/${s.dbName}/${i}`}>
                  <td className={styles.appCellMono}>{s.appId}</td>
                  <td className={styles.appCellMono}>{s.dbName}</td>
                  <td className={styles.appCellMono}>
                    {s.schemaVersion !== null ? `v${s.schemaVersion}` : "—"}
                  </td>
                  <td className={styles.appCellMono}>
                    {s.tableCount ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      {recentLogs.length > 0 ? (
        <>
          <h3 className={styles.sectionHeading}>
            Recent activity ({recentLogs.length})
          </h3>
          <div className={styles.logList}>
            {recentLogs.map((e, i) => (
              <div key={i} className={styles.logRow}>
                <span
                  className={styles.logTime}
                  title={absoluteTime(e.ts)}
                >
                  {new Date(e.ts).toLocaleTimeString()}
                </span>
                <code className={styles.logKind}>{e.kind}</code>
                <span className={styles.logMessage}>{e.message}</span>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {diagnostics !== null ? (
        <details className={styles.diagRawDetails}>
          <summary>Raw diagnostics JSON</summary>
          <pre className={styles.body}>
            {JSON.stringify(diagnostics, null, 2)}
          </pre>
        </details>
      ) : null}
    </article>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.metaRow}>
      <dt className={styles.metaLabel}>{label}</dt>
      <dd className={styles.metaValue}>{value}</dd>
    </div>
  );
}

function safeParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}
